import { useOrderSet, type VisitDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { SquareArrowOutUpRight, X } from "lucide-react";
import { useDataStoreVisitDetailController, VisitDetail } from "@/features/visits";
import { usePresentedOrderSets } from "@/lib/use-order-set-presentation";

interface AppointmentDetailPanelProps {
  visitId: string | null;
  open: boolean;
  onClose: () => void;
  visit?: VisitDto | null;
  scheduleId?: string | null;
}

export function AppointmentDetailPanel({
  open,
  onClose,
  visit = null,
  scheduleId = null,
}: AppointmentDetailPanelProps) {
  return (
    <div
      className={cn(
        "bg-background overflow-hidden transition-[width,border-width] duration-200 ease-in-out",
        open ? "w-70 border-l" : "w-0 border-l-0",
      )}
    >
      <div className="flex h-full w-70 flex-col">
        {visit && (
          <>
            <div className="flex items-center justify-between gap-1 px-2 pt-2 pb-3">
              <Button
                variant="outline"
                size="xs"
                render={
                  <Link
                    to="/patients/$patientId/visits/$visitId"
                    params={{ patientId: visit.patientId ?? "", visitId: visit.visitId }}
                    onClick={onClose}
                  />
                }
              >
                Open visit
                <SquareArrowOutUpRight className="size-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close panel">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-4 pt-1">
              <AppointmentDetailBody visit={visit} scheduleId={scheduleId} onActionComplete={onClose} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AppointmentDetailBody({
  visit,
  scheduleId,
  onActionComplete,
}: {
  visit: VisitDto;
  scheduleId: string | null;
  onActionComplete: () => void;
}) {
  // The panel draws the visit's timeline, which is the Order Set's steps — focus
  // mode already supplies this, the schedule panel did not. Keyed by the visit
  // rather than `visit.orderSetId`: the single-visit query does not join the
  // RequestGroup, so that field is always empty here, and `useOrderSet` already
  // resolves the set from a visit.
  const loaded = useOrderSet({ visitId: visit.visitId });
  const presentation = usePresentedOrderSets(loaded ? [loaded] : []);
  const orderSet = presentation.status === "ready" ? presentation.data[0] : undefined;
  const { value, dialogs } = useDataStoreVisitDetailController({
    visit,
    ...(orderSet ? { orderSet } : {}),
    scheduleId,
    onActionComplete,
  });
  return (
    <VisitDetail.Provider value={value}>
      <VisitDetail.Inline>
        <VisitDetail.InlineFields>
          <VisitDetail.StatusBadge />
          <VisitDetail.Date />
          <VisitDetail.Time />
          <VisitDetail.Protocol />
          <VisitDetail.Patient />
          <VisitDetail.Practitioner />
          <VisitDetail.Reason />
          <VisitDetail.Timeline />
        </VisitDetail.InlineFields>
        <VisitDetail.InlineActions>
          <VisitDetail.JoinAction />
          <VisitDetail.ActionsMenu />
        </VisitDetail.InlineActions>
      </VisitDetail.Inline>
      {dialogs}
    </VisitDetail.Provider>
  );
}
